import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { formatAssociatedPartyAddress } from '@/features/associated-parties/formatAddress'
import { ProductManufacturerSelect } from '@/features/product-catalog/components/ProductManufacturerSelect'
import { useListAssociatedParties } from '@/shared/hooks/api/associatedParties'
import {
  useDownloadPackagingItemOutputDocument,
  useStartPackagingItemDeclarationOfConformityGeneration,
  useUpdatePackagingItem,
} from '@/shared/hooks/api/packagingSuppliers'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { packagingItemDeclarationOfConformityProgressMessage } from './packagingItemDeclarationOfConformityProgressMessage'

const ASSOCIATED_PARTY_SELECT_ID = 'packaging-doc-associated-party'

type PackagingItemDeclarationOfConformityExportDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  packagingSupplierId: string
  itemId: string
  onProgressMessage: (message: string | null) => void
}

export function PackagingItemDeclarationOfConformityExportDialog({
  open,
  onOpenChange,
  packagingSupplierId,
  itemId,
  onProgressMessage,
}: PackagingItemDeclarationOfConformityExportDialogProps) {
  const { t } = useTranslation('packagingSuppliers')
  const canWriteOrgData = useCanWriteOrgData()
  const { t: tAssociatedParties } = useTranslation('associatedParties')
  const { t: tCountries } = useTranslation('countries')
  const [selectedPartyId, setSelectedPartyId] = useState('')
  const { data: associatedParties = [] } = useListAssociatedParties({
    enabled: open,
  })

  const updateMutation = useUpdatePackagingItem(packagingSupplierId, itemId)
  const generateMutation =
    useStartPackagingItemDeclarationOfConformityGeneration(
      packagingSupplierId,
      itemId
    )
  const downloadMutation = useDownloadPackagingItemOutputDocument(
    packagingSupplierId,
    itemId
  )

  useEffect(() => {
    if (!open) {
      setSelectedPartyId('')
    }
  }, [open])

  const selectedParty = useMemo(
    () => associatedParties.find((party) => party.id === selectedPartyId),
    [associatedParties, selectedPartyId]
  )

  const manufacturerPreview = useMemo(() => {
    if (selectedParty == null) {
      return null
    }
    return {
      name: selectedParty.name.trim(),
      address: formatAssociatedPartyAddress(selectedParty, {
        getCountryName: (code) => tCountries(code) || code,
        translate: (key, options) => tAssociatedParties(key, options),
      }).trim(),
    }
  }, [selectedParty, tAssociatedParties, tCountries])

  const isBusy =
    updateMutation.isPending ||
    generateMutation.isPending ||
    downloadMutation.isPending

  const canConfirm =
    canWriteOrgData &&
    manufacturerPreview != null &&
    manufacturerPreview.name.length > 0 &&
    manufacturerPreview.address.length > 0 &&
    !isBusy

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isBusy) {
        return
      }
      onOpenChange(nextOpen)
    },
    [isBusy, onOpenChange]
  )

  const handleConfirm = async () => {
    if (!canWriteOrgData || manufacturerPreview == null) {
      return
    }
    onProgressMessage(null)
    try {
      await updateMutation.mutateAsync({
        supplierIsManufacturer: false,
        manufacturerName: manufacturerPreview.name,
        manufacturerAddress: manufacturerPreview.address,
      })
      await generateMutation.mutateAsync({
        onEvent: (event) => {
          if (event.type === 'step.started') {
            const localizedMessage =
              packagingItemDeclarationOfConformityProgressMessage(event.step, t)
            if (localizedMessage != null) {
              onProgressMessage(localizedMessage)
            }
          }
        },
        onComplete: async (result) => {
          await downloadMutation.mutateAsync({
            outputDocumentId: result.outputDocumentId,
            fileName: result.fileName,
          })
        },
      })
      onOpenChange(false)
    } catch {
      toast.error(t('itemDetail.declarationOfConformity.exportFailed'))
    } finally {
      onProgressMessage(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className='sm:max-w-lg'
        showCloseButton={!isBusy}
        onEscapeKeyDown={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
        onInteractOutside={(event) => {
          if (isBusy) {
            event.preventDefault()
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t('itemDetail.declarationOfConformity.exportDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('itemDetail.declarationOfConformity.exportDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor={ASSOCIATED_PARTY_SELECT_ID}>
              {t('itemDetail.declarationOfConformity.exportDialog.partyLabel')}
            </Label>
            <p className='text-xs text-muted-foreground'>
              {t(
                'itemDetail.declarationOfConformity.exportDialog.partyDescription'
              )}
            </p>
            <ProductManufacturerSelect
              value={selectedPartyId}
              onValueChange={setSelectedPartyId}
              disabled={isBusy}
              enabled={open}
              inForm={false}
              triggerId={ASSOCIATED_PARTY_SELECT_ID}
            />
          </div>
          {manufacturerPreview != null && (
            <div className='space-y-2 rounded-md border bg-muted/30 p-3 text-sm'>
              <p className='font-medium'>
                {t(
                  'itemDetail.declarationOfConformity.exportDialog.previewTitle'
                )}
              </p>
              <p>{manufacturerPreview.name}</p>
              <p className='text-muted-foreground'>
                {manufacturerPreview.address}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            disabled={isBusy}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t('itemDetail.declarationOfConformity.exportDialog.cancel')}
          </Button>
          <Button
            type='button'
            disabled={!canConfirm}
            onClick={() => {
              void handleConfirm()
            }}
          >
            {isBusy && <Loader2 className='size-4 animate-spin' />}
            {t('itemDetail.declarationOfConformity.exportDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
